import type { Question } from './schemas/question'
import type { Survey } from './schemas/survey'
import type { Topic } from './schemas/topic'

export interface ISurveyCollection {
  asArray: () => Survey[]
  getSurveyById: (surveyId: string | undefined) => Survey | undefined
  getSurveyByTopicId: (topicId: string | undefined) => Survey | undefined
  getTopicById: (topicId: string | undefined) => Topic | undefined
  getQuestion: (
    topicId: string | undefined,
    questionId: string | undefined
  ) => Question | undefined
  getQuestionsForTopic: (topic: Topic) => Question[]
}

type TopicWithSurveyId = {
  survey: Survey
  topic: Topic
}

type TopicMap = Map<string, TopicWithSurveyId>

const questionMapKey = (topicId: string, questionId: string): string =>
  `${topicId}:${questionId}`

class SurveyCollection implements ISurveyCollection {
  private surveys: Survey[]
  private surveyMap: Map<string, Survey>
  private topicMap: TopicMap
  private questionMap: Map<string, Question>

  constructor(surveyList: Survey[]) {
    this.surveys = surveyList
    this.surveyMap = new Map(this.surveys.map(survey => [survey.id, survey]))
    this.topicMap = new Map<string, TopicWithSurveyId>()
    this.questionMap = new Map<string, Question>()
    this.iterateTopics(({ survey, topic }) => {
      this.topicMap.set(topic.id, { survey, topic })
    })
    this.iterateTopics(({ topic }) => {
      this.getQuestionsForTopic(topic).forEach(question => {
        this.questionMap.set(questionMapKey(topic.id, question.id), question)
      })
    })
  }

  public asArray(): Survey[] {
    return this.surveys
  }

  public getSurveyById(surveyId: string | undefined): Survey | undefined {
    return surveyId ? this.surveyMap.get(surveyId) : undefined
  }

  public getSurveyByTopicId(topicId: string | undefined): Survey | undefined {
    return topicId ? this.topicMap.get(topicId)?.survey : undefined
  }

  public getTopicById(topicId: string | undefined): Topic | undefined {
    return topicId ? this.topicMap.get(topicId)?.topic : undefined
  }

  public getQuestion(
    topicId: string | undefined,
    questionId: string | undefined
  ): Question | undefined {
    if (!topicId || !questionId) return undefined
    return this.questionMap.get(questionMapKey(topicId, questionId))
  }

  public getQuestionsForTopic(topic: Topic): Question[] {
    const rootQuestions = topic.sections.flatMap(s => s.questions)
    const subtopicQuestions = topic.subtopics.flatMap(subtopic =>
      subtopic.sections.flatMap(s => s.questions)
    )
    return [...rootQuestions, ...subtopicQuestions]
  }

  private iterateTopics(
    callback: (topicWithSurveyId: TopicWithSurveyId) => void
  ): void {
    this.surveys.forEach(survey =>
      survey.topics.forEach(topic => callback({ survey, topic }))
    )
  }
}

export default SurveyCollection
